import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CellClickedEvent, CellValueChangedEvent, GetRowIdParams, GridApi, RowNode } from "ag-grid-community";
import { useEffect, useMemo, useRef } from "react";
import { MemoType } from "../../common/enums/MemoType";
import { SetPointEventTypeEnum } from "../../common/enums/SetPointEventTypeEnum";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ITagValue } from "../../common/models/ITagValue";
import { getDeviceRoute } from "../../common/utilities/RouteHelpers";
import { NumericCellEditor } from "../../components/ag-grid-extensions/cell-editors/NumericCellEditor";
import { ContentContainer } from "../../components/content-container/ContentContainer";
import { ContentSection } from "../../components/content-section/ContentSection";
import { ContentView } from "../../components/content-view/ContentView";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import LoadingZone from "../../components/loading-zone/LoadingZone";
import OptionsSideSheet from "../../components/options-side-sheet/OptionsSideSheet";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useDeleteMemo } from "../../queries/manual-entry/UseDeleteMemo";
import { useDeleteTagValue } from "../../queries/manual-entry/UseDeleteTagValue";
import { useGetGroupManualEntryValues } from "../../queries/manual-entry/UseGetGroupManualEntryValues";
import { useSaveMemo } from "../../queries/manual-entry/UseSaveMemo";
import { useUpdateTagValues } from "../../queries/manual-entry/UseUpdateTagValues";
import GroupManualEntryLegend from "./GroupManualEntryLegend";
import useGroupManualEntryState from "./GroupManualEntryState";
import { RowData, createGridColumns, createGridRows } from "./GroupManualEntryUtils";
import { ActionMenuItem, BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import { useProgressContext } from "../../contexts/progress-context/ProgressContext";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import UUID from "../../common/utilities/Uuid";
import { ActionTypeEnum, StatusEnum } from "../../common/IExportNotificationMsg";
import { useTranslation } from "react-i18next";
import ManualEntryDataService from "../../services/manual-entry-data-service/ManualEntryDataService";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { RestError } from "../../services/rest-service/RestService";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import DefaultGrid, { DefaultGridRef } from "../../components/default-grid/DefaultGrid";
import { useUserDefaultViews } from "../../queries/user-default-view/UseUserDefaultViews";
import { routesByViewID } from "../../routes/routes";
import { isMobile, isTablet } from "react-device-detect";
import ManualEntryFormDialog, { bindManualEntryFormDialog, useManualEntryFormDialogState } from "../manual-entry-form-view/ManualEntryFormDialog";
import NoData from "../../components/no-data/NoData";
import DefaultDatePicker from "../../components/default-date-picker/DefaultDatePicker";

export default function GroupManualEntryView() {
    const [userState] = useUserContext();
    const routeContext = useRouteContext();
    const navigateTo = useNavigateTo();
    const gridApi = useRef<DefaultGridRef<RowData>>(null);
    const [,setAppNotificationState] = useAppNotificationContext();
    const [progressState, actionStarted] = useProgressContext();
    const { t } = useTranslation();
    const isPhone = isMobile && !isTablet;

    const manualEntryDialog = useManualEntryFormDialogState();



    const {
        state: {
            viewOptionState,
            actions,
            date,
            downloadUid
        },
        setActions,
        setDate,
        setDownloadUid,
        resetState
    } = useGroupManualEntryState();

    const {
        data,
        status,
        refetch
    } = useQueryNotifications(useGetGroupManualEntryValues)({
        groupID: routeContext.groupID as number,
        date: date
    });

    const {
        mutateAsync: updateTagValues,
    } = useQueryNotifications(useUpdateTagValues)({
        groupID: routeContext.groupID as number,
        convertToContractDay: true
    });

    const {
        mutateAsync: deleteTagValue,
    } = useQueryNotifications(useDeleteTagValue)({
        groupID: routeContext.groupID as number,
        convertToContractDay: true
    });

    const {
        mutateAsync: saveMemo,
    } = useQueryNotifications(useSaveMemo)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        offline: false
    });

    const {
        mutateAsync: deleteMemo
    } = useQueryNotifications(useDeleteMemo)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        offline: false
    });

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: false
    });

    const { data: defaultViews } = useQueryNotifications(useUserDefaultViews)({
    });

    const deviceViewID = useMemo(() => {
        return defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                    o.user?.id === userState.user?.id)?.viewID ?? 
                defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                !o.user)?.viewID ??
            UIViewEnum.Device_Details;

    }, [
        defaultViews,
        userState.user?.id
    ]);


    const columns = useMemo(() => {
        return data
            ? createGridColumns(date, data, isPhone)
            : [];
    }, [
        date,
        data,
        isPhone
    ]);

    const rows = useMemo(() => {
        return data
            ? createGridRows(data)
            : [];
    }, [
        data
    ]);

    const noData = ((data?.devices.length ?? 0) === 0 || (data?.tagAssociationTypes.length ?? 0) === 0) && status === 'success';

    function onDateChanged(date: Date | null) {
        if (date) {
            setDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
        }
    };

    function onCellClicked(event: CellClickedEvent<RowData>): void {

        // navigate to device manual entry when using a phone
        if (isPhone && event.data) {
            if (event.data.tags.length > 0) {
                onDeviceManualEntry(event.data.deviceID, event.data.deviceName);
            }

            return;
        }

        if (event.colDef.field === "deviceName") {
            const deviceID = event.data?.deviceID;

            const route = routesByViewID.get(deviceViewID);
            
            const url = getDeviceRoute(isPhone && !route.allowMobile ? 
                            UIViewEnum.Device_Details_Values : 
                            deviceViewID, 
                            routeContext.groupID, undefined, deviceID);

            if (url) {
                let options: NavigateToOptions | undefined;

                const mouseEvent = event.event as MouseEvent;
                if (mouseEvent && (mouseEvent.shiftKey || mouseEvent.ctrlKey)) {
                    options = {
                        newViewType: mouseEvent.shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
                    }
                }
        
                navigateTo(url, options);
            }
        }
    };

    async function onCellValueChanged(event: CellValueChangedEvent<RowData>) {
        if (event.colDef.field === "memoText") {
            onMemoValueChanged(event);
        } else {
            onTagValueChanged(event);
        }
    }

    async function onMemoValueChanged(event: CellValueChangedEvent<RowData>) {

        const rowNode = event.node;

        if (rowNode) {
            const rowData = { ...rowNode.data } as RowData;
            const timestamp = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0);
            const memoID: number = rowData.memoID ?? -1;

            const text = event.newValue ? (event.newValue as string).trim() : '';

            if (text && text.length > 0) {

                const memo = await saveMemo({
                    memoID: memoID,
                    timestamp: timestamp,
                    text: text,
                    memoType: MemoType.Memo,
                    object: {
                        id: event.data.deviceID,
                        objectType: eLynxObjectTypeEnum.Device
                    }
                });

                rowData.memoID = memo?.memoID;
                rowNode.setData(rowData);
            } else if (memoID) {

                await deleteMemo({
                    memoID: memoID,
                    timestamp: timestamp,
                    text: text,
                    memoType: MemoType.Memo,
                    object: {
                        id: event.data.deviceID,
                        objectType: eLynxObjectTypeEnum.Device
                    }
                });
            }
        }
    }

    async function onTagValueChanged(event: CellValueChangedEvent<RowData>) {

        // clearValidation();
        const tat = data?.tagAssociationTypes.find(o => `tat${o.id}` === event.colDef.field);

        if (tat) {
            const rowNode = event.node;

            if (rowNode) {
                const rowData = { ...rowNode.data } as RowData;
                const tag = rowData.tags?.find(t => t.tatID === tat.id);

                if (tag) {
                    // if empty, then delete manual entry value
                    if ((event.newValue ?? "") === "") {

                        // only delete manual entry values                        
                        if (tag.tagValue?.setPointEventType === SetPointEventTypeEnum.Manual) {
                            const tagValue: ITagValue = {
                                tagID: tag.id,
                                tatID: tag.tatID,
                                value: undefined,
                                timestamp: tag.tagValue?.timestamp,
                                contractDay: tag.tagValue?.contractDay,
                                unitOfMeasureID: tag.uomID,
                                setPointEventType: SetPointEventTypeEnum.Manual
                            };

                            await deleteTagValue(tagValue);

                            tag.tagValue = undefined;
                            tag.tagValues = [];
                        } else {
                            // can't delete a non-manual entry tag, restore the cell back to the original value
                            rowData[`tat${tat.id}`] = event.oldValue ?? "";
                        }
                    } else {
                        let value: number = +((event.newValue as string)?.replace(/,/g, ''));
                        let displayText: string = event.newValue ?? "";

                        // tagInfo.invalid = undefined;

                        if (tag.listID) {
                            const listText = data?.tagLookupList.find(o => o.id === tag.listID)?.listText.find(o => o.description === event.newValue);

                            if (listText) {
                                value = +(listText.value as string);
                                displayText = listText.description;
                            }
                        } else {
                            displayText = Number(event.newValue).toFixed(tag.decimalPlaces ?? 2);
                        }


                        // Bug 53147: DOC - Incorrect dates for manual entry data
                        // const timestampString = date.toISOString();
                        const copy = new Date(date.getFullYear(),
                                    date.getMonth(),
                                    date.getDate()
                                    );
                        const timestampString = copy.toISOString();

                        const newTagValue: ITagValue = {
                            value: value,
                            displayText: displayText,
                            setPointEventType: SetPointEventTypeEnum.Manual,
                            tagID: tag?.id,
                            unitOfMeasureID: tag?.uomID,
                            timestamp: timestampString
                        };

                        tag.tagValue = newTagValue;

                        await updateTagValues([newTagValue]);
                    }

                    rowNode.setData(rowData);
                    _refreshFooter(event.api);
                }
            }
        }
    };

    function _refreshFooter(api: GridApi<RowData>) {
        const footerRowNode = api.getPinnedBottomRow(0) as RowNode<RowData>;

        if (footerRowNode) {
            api.redrawRows({ rowNodes: [footerRowNode] });
        }
    }

    function onRefresh() {
        refetch();
    }

    function onExecuteAction(item: ActionMenuItem) {

        switch (item.action) {

            case BaseActionEnum.Refresh:
                onRefresh();
                break;

            case BaseActionEnum.Excel:
                onExport(ApplicationMimeTypesEnum.MSExcel2007);
                break;

            case BaseActionEnum.PDF:
                onExport(ApplicationMimeTypesEnum.PDF)
                break;

            default:
                break;
        }
    }

    async function onDeviceManualEntry(deviceID: number, deviceName: string) {
        
        await manualEntryDialog.open({
            elynxObject: {
                id: deviceID,
                objectType: eLynxObjectTypeEnum.Device,
                displayText: deviceName,
            },
            date:  date
        });

        await refetch();
    }

    function onExport(mimeType: ApplicationMimeTypesEnum) {

        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group,
            title: objectViewState?.contextObject?.displayText ?? '', 
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        try {

            ManualEntryDataService.exportGroupManualEntryData(
                routeContext.groupID as number,
                date,
                uid,
                mimeType
            );

            const items = [...actions];
            const exportAction = items.find(o => o.action === BaseActionEnum.Export);
            if (exportAction) {
                exportAction.disabled = true;
            }
            setActions(items);
        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    }

    // get progress running for current object
    const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    useEffect(() => {

        if (objectViewState?.contextObject.id !== routeContext.groupID ||
            progress?.notificationMsg.status === StatusEnum.Completed ||
            progress?.notificationMsg.status === StatusEnum.Failed) {
            setDownloadUid(undefined);
        }
    }, [
        objectViewState?.contextObject.id,
        routeContext.groupID,
        progress,
        downloadUid,
        setDownloadUid
    ]);

    useEffect(() => {

        const items = [...actions];

        const exportAction = items.find(o => o.action === BaseActionEnum.Export); 

        if (exportAction) {
            exportAction.disabled = progress && progress.uid === downloadUid;
        }

        // if (!_.isEqual(actions, items)) {
            setActions(items);
        // }
    }, [
        progress,
        setActions,
        downloadUid,
        actions
    ]);

    return (
        <ContentView>
            <ContextualAppBar
                actions={actions}
                viewOptionState={viewOptionState}
                onExecuteAction={onExecuteAction}
            />
            <LoadingZone isLoading={
                status === 'loading'
            }>
                <ContentContainer>
                    <ContentSection>
                        {noData && <NoData text='The group is not configured for manual entry.'/>}

                        {!noData && status === 'success' &&
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flexGrow: 1,
                                    overflow: "auto"
                                }}>



                                <DefaultGrid<RowData>
                                    ref={gridApi}
                                    suppressMovableColumns={true}
                                    suppressColumnVirtualisation={true}

                                    showFooter={true}

                                    components={{
                                        numericCellEditor: NumericCellEditor,
                                        // standardCellRenderer: StandardCellRenderer,
                                        // aggregateCellRenderer: AutoPinFooterCellRenderer
                                    }}
                                    rowData={rows}
                                    defaultColDef={{
                                        sortable: false,
                                        filter: "agMultiColumnFilter",
                                        resizable: true,
                                    }}
                                    columnDefs={columns}
                                    getRowId={(params: GetRowIdParams) => `${params.data.deviceID}`}
                                    singleClickEdit={!isPhone}

                                    // getRowClass={GaugeSheetUtils.getRowClass}
                                    // pinnedBottomRowData={state.totalRow ? [state.totalRow] : []}

                                    onCellValueChanged={onCellValueChanged}
                                    rowSelection={{
                                        mode: "singleRow",
                                        checkboxes: false,
                                        enableClickSelection: true
                                    }}
                                    tooltipMouseTrack={true}
                                    onCellClicked={onCellClicked}
                                />

                            </Box>}


                    </ContentSection>
                    
                    
                    <OptionsSideSheet onReset={() => {
                        resetState();
                    }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DefaultDatePicker
                                label="Manual Entry for"
                                value={date}
                                maxDate={new Date()}
                                onAccept={(date: Date | null) => onDateChanged(date)}
                                slotProps={{ textField: { variant: 'filled' } }}
                            />
                        </LocalizationProvider>

                        <GroupManualEntryLegend />
                    </OptionsSideSheet>
                
                </ContentContainer>
            </LoadingZone>
            {isPhone && <ManualEntryFormDialog {...bindManualEntryFormDialog(manualEntryDialog)} /> }
        </ContentView>
    );
}


